import {
  _objArray,
  _isString,
  _isArray,
  _forin,
  _trim,
  _get,
} from '../utils/utils';
import _ from 'lodash';

import createLogger from './../utils/createLogger';

const DEBUG = true;
const debug = createLogger(DEBUG, `utils.js`);

/** -------- request matching -------- */
const REQUEST_REGEX = /^((GET|PUT|POST|DELETE)_(.*))_(REQUEST|SUCCESS|FAILURE)$/;

function _matchRequest(type) {
  const match = type.match(REQUEST_REGEX);
  if (!match) return false;
  let [, name, method, request, state ] = match;
  let pagedMatch = request.match(/^(.*)_PAGE$/);
  let route = pagedMatch
    ? pagedMatch[1]
    : request;
  return {
    name,
    state,
    method,
    route: route.toLowerCase().replace(/_/, `/`),
    paged: !!pagedMatch,
  };
}

/**
 * encode a dictionary into a URI-compliant query string for REST call URLs
 */
function _queryString(queryDict) {
  let str = '';
  let argv = _.toPairs(queryDict || {});
  if (argv.length > 0) { str += `?`;
    argv.forEach(([key, val]) => {
      if (_.isArray(val))
        str += `${key}=${encodeURI(val.toString())}&`;
      else str += `${key}=${encodeURIComponent(val)}&`;
    }); // slice out trailing &
    str = str.slice(0, str.length - 1);
  } return str;
}

/** -------- cache management helpers -------- */
function _createStore() {
  return {
    records: {},
    _order: [],
  }
}


function _cleanJobRecord(job_data) {
  return {
    id: job_data._id,
    agency: job_data.src,
    jobid: job_data.jobid,
    title: job_data.title,
    status: job_data.status,
    posted: job_data._posted,
    company: job_data.company,
    results: job_data.results,
    location: job_data.location,
    contacts: job_data.contacts,
    inserted: job_data._inserted,
    url_agency: job_data.url_detail,
    max_ranking: job_data.max_ranking,
    url_found: job_data.job_url || null,
    search_terms: job_data.search_terms,
    company_id: job_data.company_id || null,
    min_salary: job_data.min_salary || null,
    max_salary: job_data.max_salary || null,
    search_data: job_data.search_data || null,
    careers_page: job_data.careers_page || null,
    min_experience: job_data.min_experience || 6,
    duplicate_lead: job_data.duplicate_lead || null,
    max_experience: job_data.max_experience || null,
    employment_type: job_data.employment_type || null,
    description: job_data.description_html || job_data.description || null,
  }
}


function _applyOrdering(store, keyProp=`id`) {
  const { records, _order } = store;
  if (!_isArray(_order) || _order.length <= 0)
    return [];

  return _order
    .map((key) => {
      if (records.hasOwnProperty(key)) {
        return {
          [keyProp]: key,
          ...records[key],
        }
      }
      return null;
    })
    .filter((item) => !!item);
}


function _removeRecord(id, store) {
  let { records } = store;

  if (records.hasOwnProperty(id)) {
    let record = _clone(records[id]);

    delete records[id];

    store.records = records;
    store._order = _sort(records);
    return record;
  }
}


function _updateRecord(update, store) {
  let { records } = store;
  let id = update._id;

  if (records.hasOwnProperty(id)) {
    records[id] = {
      ...records[id],
      ..._cleanJobRecord(update),
    };
  } else {
    records[id] = _cleanJobRecord(update);
  }
  store.records = records;
  store._order = _sort(records);
  return records[id];
}


function _addRecords(additions, store) {
  let { records } = store;

  additions.forEach((record) => {
    let id = record._id;
    if (records.hasOwnProperty(id))
      records[id] = {
        ...records[id],
        ..._cleanJobRecord(record),
      };
    else
      records[id] = _cleanJobRecord(record);
  });
  store.records = records;
  store._order = _sort(records);
  return store;
}


function _sort(records) {
  return Object.keys(records);
}


function _clone(src) {
  if (_isArray(src)) {
    return src.slice();

  } else if (typeof src === 'object') {
    let target = {};
    for (let prop in src) {
      if (src.hasOwnProperty(prop)) {
        if (typeof src[prop] === `object`)
          target[prop] = _clone(src[prop]);
        else
          target[prop] = src[prop];
      }
    }
    return target;
  }
  return src
}

function _filterDataToMap(data) {
  let filters = {};
  data.forEach((category) => {
    let cid = category._id;
    filters[cid] = _trim({
      id: cid,
      label: category.name,
      options: {},
      canQuery: category.query,
      query: category.query ? `` : null,
    });

    category.options.forEach((option) => {
      let oid = option._id;
      let order = option.open_jobs ? -option.open_jobs : null;
      filters[cid].options[oid] = {
        id: oid,
        label: option.name,
        active: false,
        order: order || option.order_index || 999,
      }
    });
  });
  return filters;
}


function _filterMapToArray(map) {
  let array = [];
  for (let key in map)
    if (map.hasOwnProperty(key))
      array.push({...map[key]});
  return array;
}


function _insertRecord(list, record, byId) {
  debug.enter(`insertRecord`);
  debug.log(`  record:`, record);
  debug.log(`  list:`, list);

  const updateIndex = list.findIndex((listRecord) => record._id === listRecord._id);

  if (updateIndex !== -1) {
    debug.log(`  found matching id`);
    debug.log(`    record:`, record);
    debug.log(`    listRecord:`, list[updateIndex]);
    let newRecord = {...record};
    list[updateIndex] = {
      ...list[updateIndex],
      ...newRecord
    };
    if(byId) byId[newRecord._id] = list[updateIndex];

    return true;
  } else {
    list.push(record);
    if(byId) byId[record._id] = record;
    return true;
  }
}


function _mergeLists(oldList, newList, byId) {
  debug.enter(`mergeLists`);
  debug.log(`  oldList:`, oldList);
  debug.log(`  newList:`, newList);

  if (!oldList || oldList.length < 1) {
    debug.log(`  oldList is empty; returning newList`);
    let retval = [...newList];
    if(byId) {
      retval.forEach(item => {
        byId[item._id] = item;
      })
    }
    return [...newList];
  }

  let mergedList = [...oldList];

  newList.forEach((newRecord) => {
    if (!_insertRecord(mergedList, newRecord, byId)) {
      debug.log(`  no matching id; appending`);
      debug.log(`    newRecord:`, newRecord);
      mergedList.push(newRecord);
      if(byId) byId[newRecord._id] = newRecord;
    }
  });
  return mergedList;
}


export {
  REQUEST_REGEX,
  _matchRequest,
  _isString,
  _createStore,
  _updateRecord,
  _removeRecord,
  _applyOrdering,
  _addRecords,
  _objArray,
  _isArray,
  _clone,
  _forin,
  _trim,
  _get,
  _filterDataToMap,
  _filterMapToArray,
  _insertRecord,
  _mergeLists,
  _queryString,
}





// WEBPACK FOOTER //
// ./src/data/utils.js